/**
 * 9 周年庆活动 — 切图批量预加载（模块级共享单例）
 * 应用启动（main.ts）即触发预加载，入口组件与弹窗共享同一份进度，
 * 避免各自重复发起请求；加载完成（含失败兜底，避免单张 404 阻塞）前弹窗不渲染内容，避免图片闪出
 */
import { ref, type Ref } from 'vue'

function loadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const image = new Image()
    image.onload = () => resolve()
    image.onerror = () => resolve()
    image.src = url
  })
}

interface PreloadEntry {
  loadedCount: Ref<number>
  done: Ref<boolean>
}

/** 共享缓存：同一批切图全局只预加载一次（弹窗多次开关直接复用结果） */
const sharedCache = new Map<string, PreloadEntry>()

/** 启动共享预加载（重复调用幂等，返回同一份进度） */
export function startSharedPreload(urls: string[]): PreloadEntry {
  const key = urls.join('|')
  let entry = sharedCache.get(key)
  if (entry) return entry

  const loadedCount = ref(0)
  const done = ref(false)
  Promise.all(
    urls.map(async (url) => {
      await loadImage(url)
      loadedCount.value++
    })
  ).then(() => {
    done.value = true
  })
  entry = { loadedCount, done }
  sharedCache.set(key, entry)
  return entry
}

/** 组件内使用：与全局共享同一份预加载进度 */
export function useImagePreload(urls: string[]) {
  const { loadedCount, done } = startSharedPreload(urls)
  return { loadedCount, total: urls.length, done }
}
